import { LitElement, html, css, unsafeHTML } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Skio Universal Plan Picker
 * @docs https://integrate.skio.com/
 *
 * Uses LitJS (<5kb) https://lit.dev/ for reactive web components
 */

export class SkioPlanPicker extends LitElement {
  static properties = {
    key: { type: String },

    product: { type: Object },
    productHandle: { type: String },
    selectedVariant: { type: Object },

    options: { type: Object },

    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },

    debug: { type: Boolean },
  }

  static styles = [
    css`
      .skio-plan-picker {
        display: flex;
        gap: 1rem;
        padding: 0;
        border: 0;
        font-size: 15px;
        color: black;
      }

      :host([layout='hidden']) .skio-plan-picker {
        display: none;
      }

      :host([layout='vertical']) .skio-plan-picker {
        flex-direction: column;
      }

      :host([layout='horizontal']) .skio-plan-picker {
        flex-direction: row;
        flex-wrap: wrap;
      }

      .sr-only {
        position: absolute;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
      }

      .group-container {
        display: block;
        position: relative;
        transition: border-color 0.2s ease;
        border: var(--skio-group-border-width, 1px) solid var(--skio-group-border-color, #ccc);
        border-radius: var(--skio-group-border-radius, 0);
        background-color: var(--skio-group-background-color, transparent);
        color: var(--skio-group-text-color, #000);
      }

      .group-container--selected {
        border-color: var(--skio-group-border-selected-color, #333);
        background-color: var(--skio-group-background-selected-color, transparent);
        color: var(--skio-group-text-selected-color, #000);
      }

      .group-container--last {
        order: 1;
      }

      :host([layout='horizontal']) .group-container {
        width: calc(50% - 0.5rem - calc(2 * var(--skio-group-border-width, 1px)));
      }

      .group-input {
        position: absolute;
        opacity: 0;
        width: 0px;
        height: 0px;
      }

      .group-input:focus-visible ~ .group-label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .group-label {
        display: flex;
        flex-direction: column;
        padding: 10px;
        cursor: pointer;
      }

      .group-topline {
        display: flex;
        align-items: center;
        width: 100%;

        gap: 8px;
      }

      :host([layout='horizontal']) .group-topline {
        flex-direction: column;
        text-align: center;
      }

      .skio-radio__container {
        display: flex;
        color: var(--skio-group-border-color, #333);
      }

      .group-container--selected .skio-radio__container {
        color: var(--skio-group-border-selected-color, #333);
      }

      .skio-radio {
        transform-origin: center;
        opacity: 0;
        transform: scale(0);
        transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-label:hover .skio-radio {
        opacity: 0.75;
        transform: scale(1);
      }

      .group-container--selected .group-label .skio-radio {
        opacity: 1;
        transform: scale(1);
      }

      .skio-price {
        display: flex;
        justify-content: flex-end;
        flex-wrap: wrap;
        align-items: center;
        text-align: right;
        gap: 4px;

        line-height: 1;

        margin-left: auto;
        vertical-align: middle;

        font-weight: 500;
      }

      .skio-price s {
        font-size: 13px;

        font-weight: 400;
        opacity: 0.75;
      }

      :host([layout='horizontal']) .skio-price {
        text-align: center;
        margin-right: auto;
      }

      .group-content {
        display: flex;
        flex-direction: column;
        opacity: 1;
        width: auto;
        max-height: 1000px;
        transition:
          max-height 0.15s cubic-bezier(0.4, 0, 0.2, 1),
          opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-content.margin-left {
        margin-left: 25px;
      }

      .group-content p {
        margin: 0.5rem 0 0 0;
        font-size: 13px;
        color: #000;
      }

      :host([layout='horizontal']) .group-content {
        width: 100%;
      }

      /* Hide frequency if not selected */
      .group-container:not(.group-container--selected) .group-content {
        pointer-events: none;
        opacity: 0;
        max-height: 0;
        visibility: hidden;
      }

      :host([layout='horizontal']) .group-container:not(.group-container--selected) + .group-content {
        pointer-events: none;
        opacity: 0;
        max-height: 0;
        visibility: hidden;
      }

      .group-title {
        width: 100%;
        max-width: 80%;
        line-height: 1.5;
      }

      :host([layout='horizontal']) .group-title {
        max-width: 100%;
      }

      .savings {
        color: var(--skio-discount-text-color, #fff);
      }

      .savings.bubble {
        padding: 0px 8px;
        background-color: var(--skio-discount-color, #0fa573);
        border: 1px var(--skio-discount-color, #0fa573) solid;
        border-radius: 4px;
        font-size: 12px;
        color: var(--skio-discount-text-color, #fff);

        white-space: nowrap;
      }

      .selling-plan-dropdown {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        display: flex;
        align-items: center;
        gap: 5px;
        width: 100%;
        margin-top: 5px;
        padding: 8px 30px 8px 10px;
        background-color: #f7f7f7;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px top 50%;
        background-size: 16px;
        border: 1px solid #ccc;
        border-radius: 2px;
        font-size: 14px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .selling-plan-dropdown--one {
        background-image: none;
        pointer-events: none;
        background: transparent;
      }

      .selling-plan-buttons {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(var(--skio-button-plan-selector-width, 20px), 1fr));
        gap: 10px;
        padding: 0.75rem 0;
        border: 0;
        font-size: 13px;
      }

      .selling-plan-buttons input[type='radio'] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      .selling-plan-buttons label {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border: 1px solid #ccc;
        text-align: center;
        padding: 1rem 0.5rem;
      }

      .selling-plan-buttons input[type='radio']:checked + label {
        border-color: #000;
      }

      .selling-plan-buttons input:focus-visible + label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .setupMode {
        position: relative;
        color: black;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 1rem;
        font-size: 14px;
        border-style: solid;
        border-width: 4px;
        border-image: repeating-linear-gradient(-55deg, #000, #000 20px, #ffb101 20px, #ffb101 40px) 10;
      }

      .setupMode:after {
        content: 'Setup Mode';
        position: absolute;
        top: 0;
        right: 0;
        background-color: black;
        color: white;
        padding: 0.25rem 1rem;
        font-size: 13px;
      }

      .setupMode .detection {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 10px;
      }

      .setupMode .detection small {
        grid-column: 1/-1;
      }
    `,
  ]

  constructor() {
    super()
 
    this.currency = window.Shopify.currency.active || 'USD'
    this.language = window.Shopify.locale || 'en-US'
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })

    // Start - Debug Variables
    this.debug = window?.Shopify?.designMode
    this.variantChanged = false
    // End - Debug Variables
  }

  connectedCallback() {
    super.connectedCallback()

    if (this.productHandle) {
      this.loading = true
      this.fetchProduct(this.productHandle)
    }

    this.bindFormEvents()
  }

  // SECTION: Element Templates
  radioTemplate() {
    return html`
      <div class="skio-radio__container">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1"></circle>
          <circle class="skio-radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
        </svg>
      </div>
    `
  }

  sellingPlanDropdown(group) {
    return html`
      <select
        class="selling-plan-dropdown ${this.getAvailableSellingPlans(group).length == 1 ? 'selling-plan-dropdown--one' : ''}"
        @change=${e => {
          this.selectSellingPlan(e.target.value)
        }}
      >
        ${group
          ? this.getAvailableSellingPlans(group).map(
              selling_plan => html` <option ?selected=${group.selected_selling_plan.id === selling_plan.id} value=${selling_plan.id}>${selling_plan.name}</option> `
            )
          : ''}
      </select>
    `
  }

  sellingPlanButtons(group) {
    return html`
      <fieldset class="selling-plan-buttons">
        <legend class="sr-only">Select subscription interval</legend>
        ${group
          ? this.getAvailableSellingPlans(group).map(
              selling_plan => html`
                <input
                  type="radio"
                  name="selling_plan_button"
                  value="${selling_plan}"
                  id="selling_plan_button-${selling_plan.id}"
                  @change="${e => this.selectSellingPlan(selling_plan.id)}"
                  ?checked=${group.selected_selling_plan === selling_plan}
                />

                <label for="selling_plan_button-${selling_plan.id}"> ${selling_plan.name} </label>
              `
            )
          : ''}
      </fieldset>
    `
  }

  invalidIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#D22B2B" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  validIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#0BDA51" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  setupMode() {
    return html`
      <div class="setupMode">
        Skio Plan Picker
        <small>This will not appear on the live site.</small>

        <small>Key: ${this.key}</small>

        <div class="detection">${this.product ? this.validIcon() : this.invalidIcon()} Product</div>

        <div class="detection">${this.selectedVariant ? this.validIcon() : this.invalidIcon()} Selected Variant</div>

        <div class="detection">${this.form ? this.validIcon() : this.invalidIcon()} Form</div>

        <div class="detection">${this.variantInput ? this.validIcon() : this.invalidIcon()} Variant Input</div>

        <div class="detection">
          ${this.variantChanged ? this.validIcon() : this.invalidIcon()} Variant Change Detection
          <small>Please attempt to change the selected variant.</small>
        </div>

        <small>If any of these don't pass please troubleshoot using our <a target="_blank" rel="noreferrer" href="https://integrate.skio.com/troubleshooting">troubleshooting guide</a>.</small>
      </div>
    `
  }

  groupContent(group) {
    return html`
      <div class="group-content ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector ? 'margin-left' : ''}">
        ${this.options?.selector_type == 'dropdown' ? this.sellingPlanDropdown(group) : ''}

        <!-- TODO: Make the label have the border instead of the group -->
        ${this.options?.selector_type == 'button' ? this.sellingPlanButtons(group) : ''} ${this.additionalContentText()}
      </div>
    `
  }
  // !SECTION: Element Templates

  render() {
    if (!this.viable() || this.options?.layout === 'hidden') {
      return this.debug ? this.setupMode() : ''
    }

    if (this.selectedVariant.selling_plan_allocations.length === 0 && !this.options?.show_without_subscription) {
      return this.debug ? this.setupMode() : ''
    }

    return html`
      ${this.debug ? this.setupMode() : null}

      <fieldset class="skio-plan-picker" role="radiogroup" aria-labelledby="skio-plan-picker-legend">
        <legend id="skio-plan-picker-legend" class="${!this.options?.show_legend ? 'sr-only' : ''}">${unsafeHTML(this.options?.legend_content)}</legend>

        ${!this.product.requires_selling_plan
          ? html`
              <div
                class="group-container ${this.selectedSellingPlanGroup == null ? 'group-container--selected' : ''} ${!this.options?.onetime_first ? 'group-container--last' : ''}"
                @click=${() => this.selectSellingPlanGroup(null)}
              >
                <input
                  id="one-time-${this.key}"
                  class="group-input"
                  name="skio-group-${this.key}"
                  type="radio"
                  value="One time purchase"
                  ?checked=${!this.selectedSellingPlanGroup}
                  @change=${() => this.selectSellingPlanGroup(null)}
                />

                <label class="group-label" for="one-time-${this.key}">
                  <div class="group-topline">
                    ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector ? this.radioTemplate() : ''}

                    <div class="group-title">${this.options?.onetime_title ? this.options?.onetime_title : 'One time purchase'}</div>

                    <div class="skio-price" aria-live="polite">
                      ${this.options?.show_compare_price && this.selectedVariant?.compare_at_price > this.selectedVariant.price
                        ? html`<s aria-hidden="true">${this.money(this.selectedVariant.compare_at_price)}</s>`
                        : ''}
                      ${this.money(this.selectedVariant.price)}
                    </div>
                  </div>
                </label>
              </div>
            `
          : ''}
        ${this.availableSellingPlanGroups?.length
          ? this.availableSellingPlanGroups.map(
              (group, index) => html`
                <div class="group-container ${this.selectedSellingPlanGroup == group ? 'group-container--selected' : ''}">
                  <input
                    id="group-${index}-${this.key}"
                    class="group-input"
                    name="skio-group-${this.key}"
                    type="radio"
                    value="${group.id}"
                    @change=${() => this.selectSellingPlanGroup(group)}
                    ?checked=${this.selectedSellingPlanGroup == group}
                  />

                  <label class="group-label" for="group-${index}-${this.key}">
                    <div class="group-topline">
                      ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector ? this.radioTemplate() : ''}

                      <div class="group-title">
                        ${group.name == 'Subscription' && this.options?.subscription_title
                          ? this.options?.subscription_title
                          : group.name == 'Prepaid' && this.options?.prepaid_title
                            ? this.options?.prepaid_title
                            : group.name}
                        ${this.discount(group.selected_selling_plan).percent > 0
                          ? html` <span class="savings ${this.options?.discount_style}"> ${this.discountText(group.selected_selling_plan)} </span> `
                          : html``}
                      </div>

                      <div class="skio-price" aria-live="polite">
                        ${this.options?.show_compare_price &&
                        (this.selectedVariant?.compare_at_price > this.selectedVariant.price || this.selectedVariant.price > this.price(group.selected_selling_plan, false))
                          ? html`<s aria-hidden="true">${this.money(this.selectedVariant.compare_at_price ? this.selectedVariant.compare_at_price : this.selectedVariant.price)}</s>`
                          : ''}
                        ${this.price(group.selected_selling_plan)}
                      </div>
                    </div>

                    ${this.options?.layout == 'vertical' ? this.groupContent(group) : ''}
                  </label>
                </div>
                ${this.options?.layout == 'horizontal' ? this.groupContent(group) : ''}
              `
            )
          : ''}
      </fieldset>
    `
  }

  bindFormEvents() {
    this.form = document.querySelector(`#${this.options?.form_id}`) || this.closest('form[action*="/cart/add"]')

    if (!this.form) {
      return
    }

    this.variantInput = this.form.querySelector('[name="id"]')

    if (this.variantInput) {
      this.variantInput.addEventListener('change', e => {
        this.selectedVariant = this.product.variants.find(variant => variant.id == e.target.value)
        this.variantChanged = true
      })
    }
  }

  updated(changed) {
    if (!this.viable()) return

    if (changed.has('product') && this.product) {
      this.key = this.key ? this.key : this.product.id
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      const filteredSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group =>
          selling_plan_group.app_id === 'SKIO' && !selling_plan_group.name.toLowerCase().includes('hidden-') && !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      )

      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = filteredSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
        )
      )

      if (this.options?.combine_groups) {
        this.availableSellingPlanGroups = [
          {
            name: this.options?.combined_group_name,
            selling_plans: this.availableSellingPlanGroups.flatMap(group => group.selling_plans),
            id: 'combined_group',
            app_id: 'SKIO',
          },
        ]
      }

      // TODO: Select proper group depending on what selling plan is selected

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length) {
        const url = new URL(window.location.href)
        const urlSelectedSellingPlanId = url.searchParams.get('selling_plan')

        //update each group with a default selected_selling_plan
        this.availableSellingPlanGroups.forEach(group => {
          const availableSellingPlans = this.getAvailableSellingPlans(group)

          const urlSelectedSellingPlan = availableSellingPlans.find(plan => plan.id == urlSelectedSellingPlanId)
          const nameSelectedSellingPlan = availableSellingPlans.find(plan => plan.name === this.selectedSellingPlan?.name)
          const defaultSellingPlan =
            this.options?.default_subscription && this.options.default_subscription.trim() !== ''
              ? availableSellingPlans.find(plan => plan.name.toLowerCase().includes(this.options.default_subscription.toLowerCase()))
              : null

          group.selected_selling_plan = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan || availableSellingPlans[0]

          const isAnyPlanSelected = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan

          group.selected = !!isAnyPlanSelected
        })

        if ((!this.variantChanged && this.options?.start_onetime == false) || this.product.requires_selling_plan == true || urlSelectedSellingPlanId || this.selectedSellingPlan) {
          let selectedSellingPlanGroup = this.availableSellingPlanGroups.find(group => group.selected) || this.availableSellingPlanGroups[0]
          let selectedSellingPlanId = selectedSellingPlanGroup.selected_selling_plan.id
          this.selectSellingPlan(selectedSellingPlanId)
        } else {
          this.selectSellingPlanGroup(null)
        }
      } else {
        this.selectSellingPlanGroup(null)
      }
    }

    if (changed.has('selectedSellingPlan')) {
      // update price of price elements if applicable
      document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach(el => {
        el.innerHTML = this.price(this.selectedSellingPlan)
      })

      // TODO Add compare price
      document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach(el => {
        el.innerHTML = this.price(this.selectedSellingPlan)
      })

      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          variant: this.selectedVariant,
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      })

      this.dispatchEvent(event)

      this.dispatchEvent(
        new CustomEvent('change', {
          detail: { value: this.selectedSellingPlan?.id },
          
        })
      )
    }

    this.updateForm()
    this.updateURLParams()
    this.updateExternalPrice()
    
    
  }

  // SECTION: Utility Functions
  log(...args) {
    args.unshift(`%c[skio plan picker][${this.key}]`, 'color: #8770f2;')
    console.log.apply(console, args)
  }

  error(...args) {
    args.unshift(`%c [skio plan picker][${this.key}]`, 'color: #ff0000')
    console.error.apply(console, args)
  }

  appendOrdinalSuffix(num) {
    const j = num % 10,
      k = num % 100
    if (j == 1 && k != 11) {
      return num + 'st'
    }
    if (j == 2 && k != 12) {
      return num + 'nd'
    }
    if (j == 3 && k != 13) {
      return num + 'rd'
    }
    return num + 'th'
  }

  money(price) {
    return this.moneyFormatter.format(price / 100.0)
  }

  viable() {
    const errors = []

    const sellingPlanInput = this.querySelector('input[name=selling_plan]')
    if (!sellingPlanInput) {
      errors.push('Missing selling plan input')
    }

    if (!this.product) {
      errors.push('No product found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.selectedVariant) {
      errors.push('No variant found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.form) {
      errors.push('No form found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id".')
    }
    if (!this.variantInput) {
      errors.push('No variant input found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id"')
    }

    if (errors.length == 0) return true

    this.error('Errors', errors)
    return false
  }

  getAvailableSellingPlans(group) {
    let availableSellingPlans = group.selling_plans.filter(selling_plan =>
      this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
    )

    return availableSellingPlans
  }

  // !SECTION: Utility Functions

  // Update selected selling plan group; called on click of group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group
    this.selectedSellingPlan = group?.selected_selling_plan
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(selling_plan_id) {
    const selectedGroup = this.availableSellingPlanGroups.find(group => group.selling_plans.some(plan => plan.id == selling_plan_id))
    const selectedSellingPlan = selectedGroup.selling_plans.find(plan => plan.id == selling_plan_id)

    selectedGroup.selected_selling_plan = selectedSellingPlan
    this.selectedSellingPlanGroup = selectedGroup
    this.selectedSellingPlan = selectedSellingPlan
  }
  // test = document.querySelectorAll('.bundle-variant-button');
 
  updateExternalPrice() {
      document.querySelectorAll(this.options?.external_price_selector).forEach(el => {
        this.selectedSellingPlan ? (el.innerHTML = this.price(this.selectedSellingPlan)) : (el.innerHTML = this.money(this.selectedVariant.price))
      })
      document.querySelectorAll(this.options?.external_price_selector).forEach(el => {
        this.selectedSellingPlan ? (el.innerHTML = this.price(this.selectedSellingPlan) + ' ' + this.currency) : (el.innerHTML = this.money(this.selectedVariant.price) + ' ' + this.currency)
      })

  }

  additionalContentText() {
    if (!this.options?.additional_subscription_content || this.options?.additional_subscription_content == '') return

    return unsafeHTML(
      this.options?.additional_subscription_content
        .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(this.discount(this.selectedSellingPlan).absolute) : this.discount(this.selectedSellingPlan).percent + '%')
        .replaceAll('[future_price_adjustments]', this.postCheckoutDiscountsText(this.selectedSellingPlan) || '')
    )
  }

  // SECTION: Discount Functions
  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discountText(selling_plan) {
    const discount = this.discount(selling_plan)

    return unsafeHTML(
      this.options?.discount_text
        .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
        .replaceAll('[future_price_adjustments]', this.postCheckoutDiscountsText(selling_plan) || '')
    )
  }

  discount(selling_plan) {
    if (!selling_plan) return { percent: 0, amount: 0 }
    return this.getDiscountFromPriceAdjustment(selling_plan, selling_plan.price_adjustments[0])
  }

  postCheckoutPriceAdjustments(selling_plan) {
    const { price_adjustments } = selling_plan
    const postCheckoutPriceAdjustments = price_adjustments.filter(({ position }) => position !== 1)
    const discounts = postCheckoutPriceAdjustments.map(price_adjustment => this.getDiscountFromPriceAdjustment(selling_plan, price_adjustment))
    return discounts
  }

  postCheckoutDiscountsText(sellingPlan = this.selectedSellingPlan) {
    if (!sellingPlan || !this.options?.future_price_adjustments_text || this.future_price_adjustments_text == '') return

    const postCheckoutPriceAdjustments = this.postCheckoutPriceAdjustments(sellingPlan)
    if (!postCheckoutPriceAdjustments.length) return

    return postCheckoutPriceAdjustments.map((discount, index) =>
      this.options?.future_price_adjustments_text
        .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
        .replaceAll('[order_count]', index + 1)
        .replaceAll('[order_count_ordinal]', this.appendOrdinalSuffix(index + 1))
    )
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment) {
    const discount = {
      percent: 0,
      amount: 0,
      absolute: 0,
    }

    let multiplier = 1

    let sellingPlanGroup = this.product.selling_plan_groups.find(group => group.selling_plans.some(plan => plan.id == selling_plan.id))

    if (sellingPlanGroup?.name === 'Prepaid') {
      const str = selling_plan.name // replace with your string
      const intervalDate = /\b\d+\s*(days|weeks|months|years)\b/gi
      const intervalDateMatches = str.match(intervalDate)

      if (intervalDateMatches?.length) {
        const intervals = intervalDateMatches[0].match(/\d+/g)
        multiplier = intervals[0] / (intervals[1] || 1)
        multiplier = multiplier > 1 ? multiplier : 1
      }
    }

    const price = this.selectedVariant.price
    const compareAtPrice = this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > price ? this.selectedVariant.compare_at_price : price

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = price_adjustment.value
        discount.absolute = Math.round((price * price_adjustment.value) / 100.0)
        discount.amount = Math.round((price * price_adjustment.value) / 100.0)
        break
      case 'fixed_amount':
        discount.percent = Math.round(((price_adjustment.value * 1.0) / price) * 100.0)
        discount.absolute = price_adjustment.value
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = Math.round((((compareAtPrice * multiplier - price_adjustment.value) * 1.0) / (compareAtPrice * multiplier)) * 100.0)
        discount.absolute = compareAtPrice * multiplier - price_adjustment.value
        discount.amount = price - price_adjustment.value
        break
    }

    return discount
  }
  // !SECTION: Discount Functions

  price(selling_plan, formatted = true) {
    return formatted ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount) : this.selectedVariant.price - this.discount(selling_plan).amount
  }

  // Updates element data to be registered by forms
  updateForm() {
    let $sellingPlan = this.querySelector('input[name=selling_plan]')

    if ($sellingPlan) {
      $sellingPlan.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : ''
      $sellingPlan.dispatchEvent(new Event('change'))
    }
  }

  // SECTION: Additional Functionality
  updateURLParams() {
    const url = new URL(window.location.href)

    if (this.selectedSellingPlan) {
      url.searchParams.set('selling_plan', this.selectedSellingPlan.id)
      window.history.replaceState({}, '', url.href)
    } else {
      url.searchParams.delete('selling_plan')
      window.history.replaceState({}, '', url.href)
    }
  }

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && {
          selling_plan: this.selectedSellingPlan?.id,
        }),
      },
    ]

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items,
      }),
    })
      .then(response => response.json())
      .then(response => {
        this.log('Added item to cart: ', response)
        //dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key,
          },
        })

        this.dispatchEvent(event)
      })
      .catch(error => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error)
      })
  }

  fetchProduct = handle => {
    return fetch(`/products/${handle}.js`)
      .then(response => response.json())
      .then(product => {
        this.product = product
        this.selectedVariant = product.variants[0]

        return product
      })
  }
  // !SECTION: Additional Functionality
}

if (!customElements.get('skio-plan-picker')) {
  customElements.define('skio-plan-picker', SkioPlanPicker)
}

if (window) window.SkioPlanPicker = SkioPlanPicker
